import { ReactNode, useState, Fragment, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/auth'
import { useBillingStatus } from '@/hooks/useBillingStatus'
import BillingLockoutBanner from '@/components/BillingLockoutBanner'
import BillingPolicyChangeBanner, { isBillingPolicyNoticeVisible } from '@/components/BillingPolicyChangeBanner'
import { Menu, Transition } from '@headlessui/react'
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  LifebuoyIcon,
  ChevronDownIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import { MoonIcon, SunIcon } from '@heroicons/react/24/solid'
import { BreadcrumbsProvider, BreadcrumbsBar } from '@/contexts/Breadcrumbs'
import BreadcrumbsAuto from '@/components/BreadcrumbsAuto'
import { useTheme } from '@/hooks/useTheme'
import SearchModal from '@/components/SearchModal'
import OfflineServersAlert from '@/components/OfflineServersAlert'
import CustomerTasksMenu from '@/components/CustomerTasksMenu'
import ConsoleSidebarNav from '@/components/ConsoleSidebarNav'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDiscord } from '@fortawesome/free-brands-svg-icons'
import { estimateSpend } from '@/lib/billingEstimate'
import { useBillingEstimate } from '@/lib/queries/billingQueries'
import LoadingSpinner from '@/components/LoadingSpinner'
import { supportApi } from '@/lib/supportApi'
import { isConsoleWorkspaceV2Enabled } from '@/lib/featureFlags'
import './account-hero-shell.css'
import { ContentAreaDialogRegion } from '@/components/ContentAreaDialog'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [contentDialogActive, setContentDialogActive] = useState(false)
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const isSre = user?.role === 'sre'
  const navigate = useNavigate()
  const { theme, toggle } = useTheme()
  const { billingStatus } = useBillingStatus(!isSre)
  const estimateQuery = useBillingEstimate()
  const estimatedSpend = estimateSpend(estimateQuery.data)
  const estimateLabel = estimateQuery.isError ? 'Last estimate' : 'Estimated Spend'
  const [searchOpen, setSearchOpen] = useState(false)
  const [hasSupportAdmin, setHasSupportAdmin] = useState(false)
  const [billingPolicyNoticeVisible, setBillingPolicyNoticeVisible] = useState(isBillingPolicyNoticeVisible)
  const showBillingPolicyNotice = billingPolicyNoticeVisible && !isSre
  const consoleWorkspaceV2 = isConsoleWorkspaceV2Enabled()
  const accountHero = location.pathname === '/account/profile' || location.pathname === '/account' ? 'profile' : location.pathname === '/account/backups' ? 'vault' : location.pathname === '/billing-overview' ? 'billing' : location.pathname === '/account/activity' ? 'activity' : ''
  const isServerWorkspaceRoute = /^\/servers\/[^/]+/.test(location.pathname)
  const isServerOverviewRoute = /^\/servers\/[^/]+\/overview\/?$/.test(location.pathname)

  useEffect(() => {
    if (!user) return
    supportApi.adminSession().then(session => setHasSupportAdmin(session.is_support_admin)).catch(() => setHasSupportAdmin(false))
  }, [user])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea') return
      // Match Panel shortcut: meta+/ (we'll also allow Ctrl+/ on Windows)
      const key = e.key.toLowerCase()
      if ((e.metaKey || e.ctrlKey) && (key === '/' || key === 'k')) {
        e.preventDefault()
        setSearchOpen(true)
      }
      if (key === 'escape') {
        setSearchOpen(false)
        setSidebarOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!sidebarOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previousOverflow }
  }, [sidebarOpen])

  const handleLogout = async () => {
    await logout()
  try { navigate('/login', { replace: true }) } catch { /* logout already completed */ }
  }

  // Special minimal layout for Create Server flow (no panel chrome)
  const isDeployFlow = location.pathname.startsWith('/compute/deploy')
  if (isDeployFlow) {
    return (
      <BreadcrumbsProvider>
  <div className="min-h-screen bg-gray-50 dark:bg-transparent flex flex-col">
          <main className="py-6 flex-1 pb-24">
            <div className="max-w-[2007px] mx-auto px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
              {children}
            </div>
          </main>
          <footer className="fixed bottom-0 inset-x-0 z-40 border-t border-gray-200 dark:border-[#2d2d2d] bg-white/95 dark:bg-[#1e1e1e]/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-[#1e1e1e]/70 text-xs text-gray-500 dark:text-gray-400">
            <div className="max-w-[2007px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <a href="https://discord.metrly.com" target="_blank" rel="noreferrer" aria-label="Discord" className="hover:opacity-80">
                <FontAwesomeIcon icon={faDiscord} className="h-4 w-4 text-gray-600 dark:text-gray-200" />
              </a>
              </div>
              <div className="flex items-center gap-3">
                <a className="hover:underline" href="https://console.metrly.com/support" target="_blank" rel="noreferrer">Support</a>
                <span>&copy; {new Date().getFullYear()} Metrly, Inc.</span>
              </div>
            </div>
          </footer>
        </div>
      </BreadcrumbsProvider>
    )
  }

  return (
  <BreadcrumbsProvider>
  <div className={`min-h-screen bg-gray-50 dark:bg-transparent ${consoleWorkspaceV2 ? 'console-workspace-v2-shell' : ''} ${consoleWorkspaceV2 && isServerOverviewRoute ? 'is-server-overview' : ''}`}>
      {showBillingPolicyNotice && (
        <BillingPolicyChangeBanner onDismiss={() => setBillingPolicyNoticeVisible(false)} />
      )}
      {/* Mobile sidebar */}
      <div className={`lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className={`fixed inset-x-0 bottom-0 z-50 bg-gray-950/50 ${showBillingPolicyNotice ? 'top-9' : 'top-0'}`} onClick={() => setSidebarOpen(false)}>
          <div
            className={`console-workspace-sidebar-surface relative flex h-full w-full max-w-xs flex-col text-white shadow-2xl ${consoleWorkspaceV2 ? 'bg-[#11141b]' : 'bg-gradient-to-b from-primary-700 to-primary-800'}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="absolute right-3 top-3 z-10">
              <button
                type="button"
                aria-label="Close navigation"
                className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto pb-4 pt-5">
              <div className="flex flex-shrink-0 items-center px-4">
                <Link to="/" onClick={() => setSidebarOpen(false)}>
                  <img src="/logos/metrly/metrly_white.png" alt="Metrly" className="h-8 w-auto" />
                </Link>
              </div>
              <ConsoleSidebarNav onNavigate={() => setSidebarOpen(false)} workspaceV2={consoleWorkspaceV2} />
            </div>
            <div className="flex flex-shrink-0 px-3 py-2">
              <div className="flex w-full items-center rounded-lg transition-colors hover:bg-white/5">
                <Link to="/account" onClick={() => setSidebarOpen(false)} className="flex min-w-0 flex-1 items-center rounded-lg p-1.5">
                  <span className="mr-2.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                    {(user?.first_name || user?.last_name || user?.email)?.[0]?.toUpperCase() || '?'}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-white">{(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : (user?.email || 'Account')}</span>
                    <span className="block truncate text-[11px] text-white/75">{user?.email}</span>
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="mr-1 flex items-center rounded-md p-2 text-white/80 hover:bg-white/10 hover:text-white"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden lg:fixed lg:bottom-0 lg:flex lg:flex-col ${consoleWorkspaceV2 ? 'lg:w-56' : 'lg:w-60'} ${showBillingPolicyNotice ? 'lg:top-9' : 'lg:top-0'}`}>
        <div className={`console-workspace-sidebar-surface flex min-h-0 flex-1 flex-col text-white border-r border-b border-gray-200 dark:border-[#2d2d2d] ${consoleWorkspaceV2 ? 'border-white/[0.08] bg-[#11141b]' : 'border-primary-900/40 bg-gradient-to-b from-primary-700 to-primary-800'}`}>
          <div className="flex flex-1 flex-col overflow-y-auto pb-4 pt-5">
            <div className="flex flex-shrink-0 items-center px-4">
              <Link to="/">
                <img
                  src="https://cdn.metrly.com/forum/logos/metrly_white_text.png"
                  alt="Metrly"
                  width={2172}
                  height={724}
                  className="h-8 w-auto"
                />
              </Link>
            </div>
            <div className="flex-1"><ConsoleSidebarNav workspaceV2={consoleWorkspaceV2} /></div>
          </div>
          
          {/* User section */}
  <div className="flex flex-shrink-0 border-t lg:border-t-0 border-white/20 px-3 py-2">
            <div className="group block w-full flex-shrink-0">
              <div className="flex items-center rounded-lg transition-colors hover:bg-white/5">
                <Link to="/account" className="flex min-w-0 flex-1 items-center rounded-lg p-1.5">
                  <span className="mr-2.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                    {(user?.first_name || user?.last_name || user?.email)?.[0]?.toUpperCase() || '?'}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-white">{(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : (user?.email || 'Account')}</span>
                    <span className="block truncate text-[11px] text-white/75">{user?.email}</span>
                  </span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="mr-1 flex items-center rounded-md p-2 text-white/80 hover:bg-white/10 hover:text-white"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

  {/* Main content */}
  <div data-account-hero={accountHero || undefined} className={`${accountHero ? 'account-hero-shell' : consoleWorkspaceV2 ? 'lg:pl-56' : 'lg:pl-60'} ${contentDialogActive ? 'h-dvh overflow-hidden' : consoleWorkspaceV2 ? 'h-screen overflow-hidden' : 'min-h-screen'} min-w-0 flex flex-col ${showBillingPolicyNotice ? 'pt-9' : ''}`}>
        {/* Top bar */}
    <div className={`${consoleWorkspaceV2 ? 'relative shrink-0' : `sticky ${showBillingPolicyNotice ? 'top-9' : 'top-0'}`} z-40 border-b border-gray-200 dark:border-[#2d2d2d] bg-white text-gray-900 dark:text-gray-100 ${consoleWorkspaceV2 ? 'dark:bg-[#0d1016]' : 'dark:bg-[#151515] shadow-sm'}`}>
          <div className={`flex shrink-0 items-center gap-x-4 px-4 sm:gap-x-6 sm:px-6 lg:px-8 ${consoleWorkspaceV2 ? 'h-14' : 'h-16'}`}>
            <button
              type="button"
              aria-label="Open navigation"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
      {/* Search bar */}
      {!isSre && <div className="flex flex-1 items-center">
                <div className="relative w-full max-w-xl mx-auto">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="search"
                    placeholder="Search"
        onFocus={() => setSearchOpen(true)}
        onClick={() => setSearchOpen(true)}
        readOnly
        className="w-full cursor-pointer rounded-full border border-gray-300 bg-white dark:bg-[#1f1f1f] py-2 pl-9 pr-10 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <button
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:text-gray-700"
                    title="Filters"
        onClick={() => setSearchOpen(true)}
                  >
                    <AdjustmentsHorizontalIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>}
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {import.meta.env.DEV && consoleWorkspaceV2 && (
                  <Link to="/prototype" className="hidden rounded-md border border-violet-400/20 bg-violet-500/10 px-2.5 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-500/15 dark:text-violet-200 xl:inline-flex">
                    Prototypes
                  </Link>
                )}
                {!isSre && <CustomerTasksMenu />}
                {/* Offline servers billing alert */}
                {!isSre && <OfflineServersAlert />}
                {/* Admin Link */}
                {(user?.root_admin || hasSupportAdmin) && (
                  <Link
                    to={user?.root_admin ? '/admin' : '/admin/support'}
                    className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-primary-600 to-primary-500 px-3 py-1.5 text-sm font-medium text-white hover:from-primary-700 hover:to-primary-600 shadow-sm"
                  >
                    <ShieldExclamationIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">{user?.root_admin ? 'Admin' : 'Support'}</span>
                  </Link>
                )}
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="inline-flex items-center gap-2 rounded-md bg-white dark:bg-[#2a2a2e] px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#3a3a3f] border border-gray-200 dark:border-[#2d2d2d]">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-[#37373d] dark:text-gray-100 text-xs font-semibold">
                        {(user?.first_name || user?.last_name || user?.email)?.[0]?.toUpperCase() || '?'}
                      </span>
                      <span className="hidden sm:block text-left leading-tight">
                        <span className="flex items-center gap-1">
                          {(user?.first_name || user?.last_name) ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim() : (user?.email || 'Account')}
                          <ChevronDownIcon className="h-3.5 w-3.5 opacity-60" />
                        </span>
                        {!isSre && estimatedSpend && (
                          <span className="block text-[11px] text-gray-500 dark:text-gray-400">{estimateLabel}: {estimatedSpend.amount}</span>
                        )}
                        {!isSre && !estimatedSpend && estimateQuery.isFetching && <span role="status" className="flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400"><LoadingSpinner size="sm" />Loading spend…</span>}
                      </span>
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-[100] mt-2 w-64 origin-top-right divide-y divide-gray-100 dark:divide-[#2d2d2d] rounded-md bg-white dark:bg-[#252526] shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {/* Header with name and estimated costs */}
                      <div className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 dark:bg-[#37373d] dark:text-gray-100 text-sm font-semibold">
                            {(user?.first_name || user?.username)?.[0]?.toUpperCase() || '?'}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{user?.first_name || user?.username}</div>
                            {!isSre && estimatedSpend && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{estimateLabel}: {estimatedSpend.amount}</div>
                            )}
                            {!isSre && !estimatedSpend && estimateQuery.isFetching && <span role="status" className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400"><LoadingSpinner size="sm" />Loading spend…</span>}
                          </div>
                        </div>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to={isSre ? '/account/profile' : '/account'}
                              className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex items-center gap-2 px-4 py-2 text-base text-gray-700 dark:text-gray-200`}
                            >
                              <UserCircleIcon className="h-4 w-4" />
                              My Account
                            </Link>
                          )}
                        </Menu.Item>
                        {!isSre && <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/billing-overview"
                              className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex items-center gap-2 px-4 py-2 text-base text-gray-700 dark:text-gray-200`}
                            >
                              <CreditCardIcon className="h-4 w-4" />
                              Billing
                            </Link>
                          )}
                        </Menu.Item>}
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={toggle}
                              className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex w-full items-center gap-2 px-4 py-2 text-left text-base text-gray-700 dark:text-gray-200`}
                            >
                              {theme === 'dark' ? (
                                // Current is dark; indicate switching to light
                                <SunIcon className="h-4 w-4" />
                              ) : (
                                // Current is light; indicate switching to dark
                                <MoonIcon className="h-4 w-4" />
                              )}
                              {theme === 'dark' ? 'Turn on the lights' : 'Turn off the lights'}
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <Link
                              to="/support"
                              className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex items-center gap-2 px-4 py-2 text-base text-gray-700 dark:text-gray-200`}
                            >
                              <LifebuoyIcon className="h-4 w-4" />
                              Support
                            </Link>
                          )}
                        </Menu.Item>
                      </div>
                      <div className="py-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleLogout}
                              className={`${active ? 'bg-gray-50 dark:bg-[#2a2a2e]' : ''} flex w-full items-center gap-2 px-4 py-2 text-left text-base text-red-600`}
                            >
                              <ArrowRightOnRectangleIcon className="h-4 w-4" />
                              Sign Out
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>
          {/* Breadcrumbs row inside header */}
          <div className="h-8 flex items-center px-4 sm:px-6 lg:px-8 bg-white dark:bg-[#101010] text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-[#2d2d2d]">
            <BreadcrumbsAuto />
            <BreadcrumbsBar />
          </div>
        </div>

        {/* Page content */}
    <main className={`flex min-w-0 flex-1 flex-col ${contentDialogActive ? 'min-h-0 overflow-hidden' : consoleWorkspaceV2 ? 'min-h-0 overflow-y-auto pb-4 pt-4' : 'pb-24 pt-4'}`}>
        <ContentAreaDialogRegion onActiveChange={setContentDialogActive}>
          <div className={`min-w-0 flex-1 text-gray-900 dark:text-gray-100 ${consoleWorkspaceV2 && isServerWorkspaceRoute ? 'flex flex-col' : 'mx-auto w-full max-w-[2007px] px-4 sm:px-6 lg:px-8'}`}>
            {/* Billing status banner */}
            {!isSre && billingStatus && billingStatus.status !== 'active' && (
              <div className="mb-4">
                <BillingLockoutBanner status={billingStatus} dismissible={!billingStatus.is_locked_out} />
              </div>
            )}
            {children}
          </div>
        </ContentAreaDialogRegion>
        </main>
        <footer className={`${consoleWorkspaceV2 || contentDialogActive ? 'relative shrink-0' : 'fixed bottom-0 left-0 right-0 z-40 lg:left-60'} border-t border-gray-200 bg-white/95 text-xs text-gray-500 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:border-[#2d2d2d] dark:bg-[#1e1e1e]/95 dark:text-gray-400 dark:supports-[backdrop-filter]:bg-[#1e1e1e]/70`}>
          <div className="max-w-[2007px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-3">
                <a href="https://discord.metrly.com" target="_blank" rel="noreferrer" aria-label="Discord" className="hover:opacity-80">
                  <FontAwesomeIcon icon={faDiscord} className="h-4 w-4 text-gray-600 dark:text-gray-200" />
                </a>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <a className="hover:underline" href="https://console.metrly.com/support" target="_blank" rel="noreferrer">Support</a>
              <span>&copy; {new Date().getFullYear()} Metrly, Inc.</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  {!isSre && <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />}
  </BreadcrumbsProvider>
  )
}
